import {Component, inject, OnInit} from '@angular/core';
import {LipoDetailComponent} from "../../../shared/components/lipo-detail/lipo-detail.component";
import {LipoDetailModel} from "../../../shared/models/lipo-detail.model";
import {ActivatedRoute, Router} from "@angular/router";
import {CustomerDataService} from "../../services/customer-data.service";
import {LipoRoutesDataModel} from "../../../shared/models/lipo-routes-data.model";
import {CustomerDetailModel} from "../models/customer-detail.model";
import {CustomerServiceMapper} from "../../mappers/customer-service.mapper";
import {LipoFormModel} from "../../../shared/models/lipo-form.model";
import {forkJoin, Observable, of, switchMap} from "rxjs";
import {CustomerDetailMapper} from "../../mappers/customer-detail.mapper";
import {LipoTab} from "../../../shared/components/models/lipoTab";
import {LipoFormControlModel} from "../../../shared/components/models/lipo-form-control.model";
import {LipoRouteEnum} from "../../../shared/enums/lipo-route.enum";
import {MatTableDataSource} from "@angular/material/table";
import {SelectionModel} from "@angular/cdk/collections";
import {LipoTableDisplayedColumns, LipoTableModel} from "../../../shared/components/models/lipo-table.model";
import {SystemDetailModel} from "../../../system/components/models/system-detail.model";
import {LipoModelInterface} from "../../../shared/interfaces/lipo-model.interface";
import {PartnerDataService} from "../../../partner/services/partner-data.service";
import {LipoFormMapper} from "../../../shared/mappers/lipo-form.mapper";
import {v4 as uuidv4} from "uuid";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {LipoButton} from "../../../shared/components/models/lipo-button";
import {UserDetailModel} from "../../../user/components/models/user-detail.model";
import {ProgressService} from "../../../shared/services/progress.service";
import {LipoFormGroupModel} from "../../../shared/components/models/lipo-form-group.model";
import {getAdminCustomerDetailFormFields, getCustomerDetailFormFields} from "./customer-detail.form-fields";
import {
  LipoConfirmationDialogComponent
} from "../../../shared/components/lipo-confirmation-dialog/lipo-confirmation-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {
  LipoResetPasswordDialogComponent
} from "../../../shared/components/lipo-reset-password-dialog/lipo-reset-password-dialog.component";
import {KeycloakRoleEnum} from "../../../shared/enums/keycloak-role.enum";
import Keycloak from "keycloak-js";

@Component({
  selector: 'du-customer-detail',
  standalone: true,
  imports: [
    LipoDetailComponent
  ],
  templateUrl: './customer-detail.component.html',
  styleUrl: './customer-detail.component.scss'
})
export class CustomerDetailComponent implements OnInit {
  private readonly keycloak = inject(Keycloak)

  detailModel?: LipoDetailModel;
  customerId: number | null = null;
  isSaving: boolean = false

  private readonly _SystemTableUuid: string = uuidv4();
  private readonly _UserTableUuid: string = uuidv4();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly customerService: CustomerDataService,
    private readonly snackBar: SnackbarService,
    private readonly partnerService: PartnerDataService,
    private readonly progressService: ProgressService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
  ) {
    progressService.startLoading()
  }

  ngOnInit(): void {
    this.activatedRoute.data.subscribe({
      next: data => {
        let routesDataModel = data as LipoRoutesDataModel;
        if (routesDataModel.isCreateItem) {
          this.createEmptyCustomer();
        } else {
          this.activatedRoute.paramMap.subscribe({
            next: paramMap => {
              let idParam = paramMap.get('customerId');
              if (!idParam) return;

              let id = +idParam;

              this.customerId = id
              this.loadCustomer(+id);
            }
          })
        }
      }
    });
  }

  loadCustomer(id: number): void {
    this.customerService.getCustomer(id).subscribe({
      next: value => {
        let customerDetailModel = CustomerServiceMapper.toCustomerDetailModel(value)
        this.createDetail(customerDetailModel);
      }
    })
  }

  createEmptyCustomer(): void {
    this.partnerService.getPartnersSmall().subscribe(partners => {
      const emptyCustomerModel = new CustomerDetailModel();
      emptyCustomerModel.partner = partners[0]?.id;
      this.createDetail(emptyCustomerModel);
    });
  }

  createDetail(customerModel: CustomerDetailModel): void {
    let adminForm$ = this.keycloak.hasRealmRole(KeycloakRoleEnum.ADMIN) ? this.getAdminFormFields(customerModel) : of([]);

    forkJoin({
      adminForm: adminForm$,
      baseForm: getCustomerDetailFormFields(customerModel),
    }).subscribe(({adminForm, baseForm}) => {
      let baseForms = [...baseForm, ...adminForm] as LipoFormControlModel[];

      let tabs = this.getTabs(customerModel);
      let lipoFormModel = new LipoFormModel([new LipoFormGroupModel({controls: baseForms})]);
      this.detailModel = CustomerDetailMapper.toLipoDetailModel(customerModel, lipoFormModel, tabs);

      this.progressService.stopLoading()
    });
  }

  getAdminFormFields(model: CustomerDetailModel): Observable<LipoFormControlModel[]> {
    return this.partnerService.getPartnersSmall().pipe(
      switchMap(partners => {
        return getAdminCustomerDetailFormFields(partners, model);
      })
    );
  }

  getTabs(model: CustomerDetailModel): LipoTab[] {
    return LipoTab.build(
      {
        title: 'systems',
        components: [
          {
            uuid: this._SystemTableUuid,
            component: this.getSystemTable(model.systems)
          }
        ]
      },
      {
        title: 'users',
        components: [
          {
            uuid: this._UserTableUuid,
            component: this.getUserTable(model.users)
          }
        ]
      }
    );
  }

  getUserTable(users: UserDetailModel[]) {
    let tableDataSource = new MatTableDataSource<UserDetailModel>(users);
    let selectionModel = new SelectionModel<UserDetailModel>(true, []);
    let displayedColumns = LipoTableDisplayedColumns.build(
      {HeaderCellName: 'firstName', PropertyName: 'firstName'},
      {HeaderCellName: 'lastName', PropertyName: 'lastName'},
      {HeaderCellName: 'mail', PropertyName: 'email'},
      {HeaderCellName: 'sapUser', PropertyName: 'sapUser'},
      {HeaderCellName: 'keycloak.userId', PropertyName: 'keyCloakUserId'},
    );
    let buttons = this.getUserTableButtons();

    return new LipoTableModel<UserDetailModel>(
      tableDataSource,
      displayedColumns,
      selectionModel,
      buttons,
      (value) => this.onUserRowClick(value)
    );
  }

  private getUserTableButtons(): LipoButton[] {
    if (!this.customerId) return [];

    return LipoButton.build(
      {
        text: "button.user.sync",
        icon: "sync",
        onClick: async () => this.syncKeyCloakUsers()
      },
      {
        text: "button.user.add",
        icon: "add",
        onClick: async () => this.addUsers()
      },
      {
        className: 'hide-button-as-divider',
        text: "fake.button",
        onClick: async () => undefined
      },
      {
        text: "button.userMapping.delete",
        icon: "delete",
        onClick: async () => this.onDeleteCustomerUsersClicked(),
        disableIfEmpty: true
      },
      {
        text: "button.userMapping.update.password",
        icon: "lock_reset",
        onClick: async () => this.onUpdatePasswordCustomerUsersClicked(),
        disableIfEmpty: true
      },
    );
  }

  getSystemTable(systems: SystemDetailModel[]) {
    let tableDataSource = new MatTableDataSource<SystemDetailModel>(systems)
    let selectionModel = new SelectionModel<SystemDetailModel>(true, [])
    let displayedColumns = LipoTableDisplayedColumns.build(
      {HeaderCellName: 'id', PropertyName: 'sapSystemId'},
      {HeaderCellName: 'name', PropertyName: 'name'},
      {HeaderCellName: 'hardwareKey', PropertyName: 'hardwareKey'},
      {HeaderCellName: 'initialDate', PropertyName: 'initialDate'},
      {HeaderCellName: 'status', PropertyName: 'isActiveText'},
    );
    let buttons = this.getAddSystemButton();

    return new LipoTableModel<SystemDetailModel>(
      tableDataSource,
      displayedColumns,
      selectionModel,
      buttons,
      (value) => this.onSystemRowClick(value)
    );
  }

  private syncKeyCloakUsers(): void {
    if (this.customerId) {
      let id = this.customerId

      this.customerService.syncUserToKeyCloak(id).subscribe({
        next: () => {
          this.loadCustomer(id);
          this.snackBar.Saved();
        }
      });
    }
  }

  private async addUsers(): Promise<void> {
    await this.router.navigate([LipoRouteEnum.CUSTOMER, this.customerId, LipoRouteEnum.USER, 'new'])
  }

  private onDeleteCustomerUsersClicked(): void {
    let userTab = this.detailModel?.findComponent<LipoTableModel<UserDetailModel>>(this._UserTableUuid)
    let selectedIdList = userTab?.selectionModel?.selected.map(user => user.id) ?? []

    if (selectedIdList.length === 0) return;

    this.openConfirmationDialog().subscribe({
      next: (confirmed) => {
        if (confirmed) {
          this.deleteCustomerUsers(selectedIdList);
        }
      }
    })
  }

  private onUpdatePasswordCustomerUsersClicked(): void {
    let userTab = this.detailModel?.findComponent<LipoTableModel<UserDetailModel>>(this._UserTableUuid)
    let selectedIdList = userTab?.selectionModel?.selected.map(user => user.id) ?? []

    if (selectedIdList.length === 0) return;

    const dialogRef = this.dialog.open(LipoResetPasswordDialogComponent, {
      disableClose: true,
      data: {currentPassword: null}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        selectedIdList.forEach(userId => {
          this.customerService.updatePasswordForUser(userId, {'newPassword': result}).subscribe({
            next: () => {
              this.snackBar.Success(this.translate.instant('snackbar.passwordUpdated'));
            }, error: () => {
              this.snackBar.Error(this.translate.instant('snackbar.passwordUpdateFailed'));
            }
          });
        })
      }
    });
  }

  private openConfirmationDialog(): Observable<boolean> {
    const dialogRef = this.dialog.open(LipoConfirmationDialogComponent, {
      data: {
        title: this.translate.instant('dialog.title.delete'),
        content: this.translate.instant('dialog.content.delete'),
        confirmButtonText: this.translate.instant('button.delete')
      },
    });
    return dialogRef.afterClosed();
  }

  private deleteCustomerUsers(selectedIdList: (number | null)[]): void {
    this.customerService.deleteCustomerUsers(this.customerId!, selectedIdList).subscribe({
      next: (successfully) => {
        if (successfully.length === 0) {
          this.snackBar.Deleted();
        } else {
          this.snackBar.Warning(this.translate.instant('snackbar.someNotDeleted', {amount: successfully.length}))
        }
        this.loadCustomer(this.customerId!);
      }
    })
  }

  private getAddSystemButton(): LipoButton[] {
    if(!this.customerId) return [];

    return LipoButton.build({
      text: "button.system.add",
      icon: "add",
      onClick: async () => {
        await this.router.navigate([LipoRouteEnum.SYSTEM, 'new'], {
          queryParams: { customerId: this.customerId }
        });
      }
    });
  }

  async onSystemRowClick(value: LipoModelInterface) {
    let id = value.getId();

    if (id) {
      await this.router.navigate([LipoRouteEnum.SYSTEM, id]);
    }
  }

  async onUserRowClick(value: LipoModelInterface) {
    let id = value.getId();

    if (id) {
      await this.router.navigate([LipoRouteEnum.CUSTOMER, this.customerId, LipoRouteEnum.USER, id]);
    }
  }

  onDeleteClicked(id: number | null): void {
    if (id === null) return;

    this.customerService.deleteCustomer(id).subscribe({
      next: (successfully) => {
        if (successfully) {
          this.router.navigate([LipoRouteEnum.CUSTOMER]).then(() => this.snackBar.Deleted());
        }
      }
    })
  }

  onSaveClick(detailModel: LipoDetailModel, id: number | null): void {
    if (!detailModel.form?.formGroup) return;

    let systemTab = detailModel.findComponent<LipoTableModel<SystemDetailModel>>(this._SystemTableUuid)
    let userTab = detailModel.findComponent<LipoTableModel<UserDetailModel>>(this._UserTableUuid)
    let serviceCustomerModel = LipoFormMapper.toCustomerServiceModel(detailModel.form?.formGroup, undefined, systemTab?.tableDataSource.data, userTab?.tableDataSource.data);

    this.isSaving = true
    if (id !== null && id > 0) {
      serviceCustomerModel.id = id;
      this.customerService.updateCustomer(serviceCustomerModel).subscribe({
        next: customer => {
          this.createDetail(CustomerServiceMapper.toCustomerDetailModel(customer));
          this.snackBar.Saved();
        },
        error: () => this.isSaving = false,
        complete: () => this.isSaving = false
      });
    } else {
      this.customerService.createCustomer(serviceCustomerModel).subscribe({
        next: customer => {
          this.router.navigate([LipoRouteEnum.CUSTOMER, customer.id]).then(() => this.snackBar.Saved());
        },
        error: () => this.isSaving = false,
        complete: () => this.isSaving = false
      });
    }
  }

  protected readonly LipoRouteEnum = LipoRouteEnum;
}
